import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';

import type { APIData, PopupData, Service } from 'webapp-types';
import { LEGACY_METRIC_MIGRATION_HELP_URL, LegacyMetricPopupType } from 'webapp/enums/metric-mover';
import { TrackingEvent } from 'webapp/enums/tracking';
import {
	formatLegacyDeletionDate,
	getLegacyPopupSourceLines,
} from 'webapp/utils/legacy-metric-deletion';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type MetricMoverService from 'webapp/services/metric-mover';

export default class MetricMoverLegacyDeletionPopupComponent extends PopupInnerModalComponent<PopupData.MetricMover.LegacyDeletion> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('metric-mover') declare metricMoverService: MetricMoverService;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	readonly migrationHelpUrl = LEGACY_METRIC_MIGRATION_HELP_URL;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.analyticsService.event(
			this.isPushApi
				? TrackingEvent.METRIC_MOVER_PUSHAPI_POPUP_SHOWN
				: TrackingEvent.METRIC_MOVER_POPUP_SHOWN,
			this.trackingProps,
		);
	}

	get isPushApi(): boolean {
		return this.popupData.type === LegacyMetricPopupType.PUSH_API;
	}

	get title(): string {
		return this.isPushApi
			? 'Your legacy Push API metrics have been migrated'
			: 'Your legacy metrics have been migrated';
	}

	get formattedDate(): string {
		return formatLegacyDeletionDate(this.popupData.deletionDate);
	}

	get sourceLines(): string[] {
		return getLegacyPopupSourceLines(this.popupData.sourceNames);
	}

	get trackingProps(): APIData.Tracking.Data {
		const { spaceId, deletionDate, daysUntilDeletion, sourceNames, userRole } = this.popupData;

		return {
			account_id: spaceId,
			deletion_date: deletionDate,
			days_until_deletion: daysUntilDeletion,
			data_source_count: sourceNames.length,
			user_role: userRole,
		};
	}

	private markSeen(): void {
		this.userSettingsService.markLegacyMetricPopupSeen(
			this.popupData.type,
			this.popupData.spaceId,
		);
	}

	// "Got it" and the close X
	@action
	dismiss(): void {
		this.markSeen();
		this.close();
	}

	@action
	reviewMetrics(): void {
		// Marks the popup seen when it opened. Otherwise (metric mover not configured) the popup
		// closes unseen and shows again on a later load.
		if (this.metricMoverService.openLegacyMetricReview(this.popupData.type)) {
			this.analyticsService.event(
				this.isPushApi
					? TrackingEvent.METRIC_MOVER_PUSHAPI_REVIEW
					: TrackingEvent.METRIC_MOVER_POPUP_REVIEW,
				this.trackingProps,
			);
		}

		this.close();
	}

	@action
	chatWithSupport(): void {
		this.currentUserService.chatWithSupport();
	}

	<template>
		<div class="content">
			<h3>{{this.title}}</h3>
			{{#if this.isPushApi}}
				<p>
					We've prepared datasets and new metrics for these data sources and copied over
					your historical data:
				</p>
			{{else}}
				<p>
					We've migrated the legacy metrics in these data sources to datasets and replaced
					them with new metrics:
				</p>
			{{/if}}
			<ul class="legacy-deletion-popup__sources">
				{{#each this.sourceLines as |line|}}
					<li>{{line}}</li>
				{{/each}}
			</ul>
			{{#if this.isPushApi}}
				<p>
					Your legacy metrics will keep receiving data until
					{{this.formattedDate}}. Before then, make sure the new datasets contain all your
					data, including anything you pushed to your legacy metrics after we copied your
					historical data. Then update your code to push data to the new datasets. The
					<a href={{this.migrationHelpUrl}} target="_blank">in-app migration tool</a>
					can help you through these changes.
				</p>
				<p>
					We'll start deleting the legacy metrics after
					{{this.formattedDate}}
					and will keep only the new ones.
				</p>
			{{else}}
				<p>
					Your historical data and dashboards carry over, so there's nothing to rebuild.
				</p>
				<p>
					Please review your new metrics. We'll start deleting the legacy metrics after
					{{this.formattedDate}}
					and will keep only the new ones.
				</p>
			{{/if}}
			<p>
				Need help? Contact our Support team through
				<a role="button" {{on "click" this.chatWithSupport}}>in-app chat</a>.
			</p>
			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Got it"
						@extraClass="legacy-deletion-popup__got-it"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.dismiss}}
					/>
					<SubmitButtonComponent
						@text="Review metrics"
						@extraClass="legacy-deletion-popup__review"
						@noDelay={{true}}
						@onTrigger={{this.reviewMetrics}}
					/>
				</div>
			</div>
		</div>

		<a role="button" class="close" {{on "click" this.dismiss}} aria-label="Close">
			<CommonUiIconComponent @name="Close" @size="lg" />
		</a>
	</template>
}
